import { Suspense } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Collapsible from '@radix-ui/react-collapsible';
import { fetchCampaignCustomFields } from 'api/campaigns';
import { TransactionEditableCustomField } from 'assets/js/pages/transactions/TransactionEditableCustomField';
import { useCombinedFields } from 'customHooks/useCombinedFields';
import { useCurrentTransactionOrById } from 'customHooks/useCurrentTransactionOrById';
import { campaignQueryKeys } from 'queries/campaign-query-keys';
import { Transaction } from 'types/domain';

interface TransactionCustomFieldsProp {
    transaction: Transaction;
    campaignId: number;
}

const TransactionCustomFields = (props: TransactionCustomFieldsProp) => {
    const { transaction, campaignId } = props;

    const { data: customFields = [] } = useSuspenseQuery({
        queryKey: campaignQueryKeys.customFields(campaignId),
        queryFn: () => fetchCampaignCustomFields(campaignId),
    });

    const combinedFields = useCombinedFields(customFields, transaction?.custom_fields || []);

    if (!combinedFields.length) {
        return null;
    }

    return (
        <Collapsible.Root defaultOpen={true}>
            <Collapsible.Trigger
                className={
                    'group flex w-full items-center justify-between gap-2 rounded-md bg-gray-100 px-3 py-2'
                }
            >
                <p className={'text-sm font-medium'}>Custom fields</p>
                <FontAwesomeIcon
                    className={'group-radix-state-open:rotate-180'}
                    icon={faChevronDown}
                />
            </Collapsible.Trigger>
            <Collapsible.Content className={'flex flex-col gap-6 px-3 py-4'}>
                {combinedFields.map(field => (
                    <TransactionEditableCustomField
                        key={field.id}
                        field={field}
                        transaction={transaction}
                    />
                ))}
            </Collapsible.Content>
        </Collapsible.Root>
    );
};

const WrappedTransactionCustomFields = () => {
    const { data: transaction } = useCurrentTransactionOrById();

    const campaign = transaction?.campaign;

    if (!campaign) {
        return null;
    }

    return (
        <Suspense fallback={null}>
            <TransactionCustomFields campaignId={campaign.id} transaction={transaction} />
        </Suspense>
    );
};

export { WrappedTransactionCustomFields as TransactionCustomFields };
